import PubSub from "underpub-js";

/**
 * AvailabilityMap
 * Floor-image gallery + pin / popup overlay with floor and residence-type filters.
 *
 * Floor index conventions:
 * - Internal index is 0-based (used to index $floorImages and currentFloorIndex).
 * - DOM data-floor-index attributes, PubSub event payloads, and select <option> values
 *   are 1-based ("1" = Floor 1).
 */

var PIN_RESIZE_DEBOUNCE_MS = 100;
// Late-binding popups subscribe a tick after init runs; delay the initial publish
// so they don't miss it.
var INITIAL_FLOOR_PUBLISH_DELAY_MS = 50;
// Webflow may reinitialize selects after our init runs; defer the default-value
// sync to the next tick so the visible value isn't wiped on initial load.
var FLOOR_SELECT_SYNC_DELAY_MS = 10;

var POPUP_SELECTORS = '[data-amp-component="PopupUnit"], [data-amp-component="PopupItem"]';
var POPUP_WRAPPER_SELECTORS = '.popup-unit_wrapper, .popup-item_wrapper';
var POPUP_INNER_SELECTORS = '.popup-unit_card-wrapper, .popup-item_card-wrapper, .popup-unit_pointer, .popup-item_pointer';

var instanceCounter = 0;

function toOneBased(zero) { return zero + 1; }
function toZeroBased(one) { return one - 1; }

var AvailabilityMap = function(options) {
	this.options = {
		$el: false,
		baseClass: 'mol-AvailabilityMap'
	};
	$.extend(true, this.options, options);

	this.instanceId = ++instanceCounter;

	this.$imageWrapper = null;
	this.$dummyImage = null;
	this.$floorImages = $();
	this.currentFloorIndex = null;

	this.selectedResidenceType = null;
	this.$allPopups = $();

	this._pinPositionRaf = null;
	this._resizeTimer = null;
};

AvailabilityMap.prototype.init = function() {
	var self = this;

	// Subscribe before any state changes so late subscribers don't miss the first publish.
	PubSub.subscribe('availabilityMap:requestCurrentFloor', function() {
		if (self.currentFloorIndex !== null) {
			PubSub.publish('availabilityMap:currentFloor', {
				floorIndex: toOneBased(self.currentFloorIndex)
			});
		}
	});
	PubSub.subscribe('availabilityMap:syncPinPositions', function() {
		self.updatePinPositions();
	});

	this.initGallery();
	this.bindPinPositionEvents();

	this.$allPopups = this.options.$el.find('[data-amp-component="PopupUnit"]');
	if (!this.$allPopups.length) {
		console.warn('AvailabilityMap: No popups found for filtering');
	}

	this.setInitialFloor(this.getDefaultFloorIndex());
	this.schedulePinPositionUpdate();
	this.setupFilterControls();
	this.setupResetButton();
};

/**
 * Initialize the background image gallery.
 * The dummy image (`.dummy`) is purely a height anchor so the absolutely-positioned
 * floor images have a sized box to render into. Initial visibility is handled in
 * SCSS (`.availability-map_image:not(.dummy) { opacity: 0; ... }` flips to opaque
 * via the `.active` class).
 */
AvailabilityMap.prototype.initGallery = function() {
	this.$imageWrapper = this.options.$el.find('.availability-map_image-wrapper');
	if (!this.$imageWrapper.length) {
		console.warn('AvailabilityMap: Image wrapper not found');
		return;
	}
	this.$imageWrapper.css({ position: 'relative' });

	var $allImages = this.$imageWrapper.find('.availability-map_image');
	if (!$allImages.length) {
		console.warn('AvailabilityMap: No images found in image wrapper');
		return;
	}

	this.$dummyImage = $allImages.filter('.dummy').first();
	if (!this.$dummyImage.length) {
		console.warn('AvailabilityMap: Dummy image not found');
		return;
	}

	this.$floorImages = $allImages.not('.dummy').removeClass('active');
	this.bindFloorImageLoadsForPins();
};

/**
 * Recompute pin positions once each floor image is decoded. Twig outputs raw
 * normalized coords that need mapping through the object-fit: contain rect.
 */
AvailabilityMap.prototype.bindFloorImageLoadsForPins = function() {
	var self = this;
	if (!this.$floorImages.length) return;

	this.$floorImages.each(function() {
		var img = this;
		var onReady = function() { self.schedulePinPositionUpdate(); };
		if (img.complete && img.naturalWidth) {
			onReady();
		} else {
			$(img).one('load', onReady);
		}
	});
};

AvailabilityMap.prototype.bindPinPositionEvents = function() {
	var self = this;
	// Per-instance namespace so multiple maps on a page don't clobber each other.
	var ns = '.availabilityMapPins-' + this.instanceId;
	$(window).on('resize' + ns, function() {
		clearTimeout(self._resizeTimer);
		self._resizeTimer = setTimeout(function() {
			self.updatePinPositions();
		}, PIN_RESIZE_DEBOUNCE_MS);
	});
};

AvailabilityMap.prototype.schedulePinPositionUpdate = function() {
	var self = this;
	if (this._pinPositionRaf) {
		cancelAnimationFrame(this._pinPositionRaf);
	}
	this._pinPositionRaf = requestAnimationFrame(function() {
		self._pinPositionRaf = null;
		self.updatePinPositions();
	});
};

/**
 * Resolve `object-position` to (x, y) offsets in px against the letterbox excess.
 */
AvailabilityMap.prototype._objectPositionOffsets = function(pos, excessX, excessY) {
	var parts = String(pos || '').trim().split(/\s+/);
	return {
		ox: resolveObjectPositionAxis(parts[0] || '50%', excessX, 'horizontal'),
		oy: resolveObjectPositionAxis(parts.length > 1 ? parts[1] : '50%', excessY, 'vertical')
	};
};

function resolveObjectPositionAxis(val, excess, axis) {
	var startKw = axis === 'horizontal' ? 'left' : 'top';
	var endKw = axis === 'horizontal' ? 'right' : 'bottom';

	if (val === startKw || val === '0%') return 0;
	if (val === endKw || val === '100%') return excess;
	if (val === 'center') return excess / 2;

	var pct = /^([\d.]+)%$/.exec(val);
	if (pct) return excess * (parseFloat(pct[1], 10) / 100);

	var px = /^([\d.]+)px$/.exec(val);
	if (px) return parseFloat(px[1], 10);

	return excess / 2;
}

/**
 * Pins store image-normalized coords (0–1). With `object-fit: contain` the drawable
 * rect is letterboxed, so we map normalized coords to overlay percentages using
 * the rendered rect's actual size and origin.
 */
AvailabilityMap.prototype.updatePinPositions = function() {
	if (!this.$imageWrapper || !this.$imageWrapper.length) return;

	var $pins = this.options.$el.find('[data-map-nx][data-map-ny]');
	if (!$pins.length) return;

	var self = this;

	$pins.each(function() {
		var $pin = $(this);
		var nx = parseFloat($pin.attr('data-map-nx'), 10);
		var ny = parseFloat($pin.attr('data-map-ny'), 10);
		if (isNaN(nx) || isNaN(ny)) return;

		var floorOneBased = parseInt($pin.attr('data-floor-index'), 10);
		if (isNaN(floorOneBased)) return;

		var $img = self.$imageWrapper
			.find('.availability-map_image[data-floor-index="' + floorOneBased + '"]')
			.not('.dummy')
			.first();
		if (!$img.length) return;

		var img = $img[0];
		var nw = img.naturalWidth;
		var nh = img.naturalHeight;
		var cw = img.clientWidth;
		var ch = img.clientHeight;
		if (!nw || !nh || !cw || !ch) {
			// Force-decode lazy images so pins can be positioned on first reveal.
			if ('loading' in img && img.loading === 'lazy') img.loading = 'eager';
			return;
		}

		var scale = Math.min(cw / nw, ch / nh);
		var rw = nw * scale;
		var rh = nh * scale;
		var off = self._objectPositionOffsets(
			window.getComputedStyle(img).objectPosition,
			cw - rw,
			ch - rh
		);

		$pin.css({
			left: ((off.ox + nx * rw) / cw) * 100 + '%',
			top: ((off.oy + ny * rh) / ch) * 100 + '%'
		});
	});

	PubSub.publish('availabilityMap:pinsPositioned');
};

/**
 * Apply current filters to popups. We use inline visibility (rather than a class)
 * so we can also stop pointer events on non-active floors without depending on
 * rule specificity against BasePopup's own inline styles.
 */
AvailabilityMap.prototype.applyFilters = function() {
	if (!this.$allPopups.length) return;

	var self = this;
	var activeFloor = this.currentFloorIndex !== null
		? toOneBased(this.currentFloorIndex)
		: null;

	this.$allPopups.each(function() {
		var $popup = $(this);
		var matchesType = self._popupMatchesResidenceType($popup);
		var popupFloor = parseInt($popup.attr('data-floor-index'), 10);
		var onActiveFloor = activeFloor !== null && !isNaN(popupFloor) && popupFloor === activeFloor;

		if (matchesType) {
			$popup.css({ display: 'block', opacity: 1 });
			$popup.css('pointerEvents', onActiveFloor ? 'auto' : '');
		} else {
			$popup.css({ display: 'none', opacity: 0, pointerEvents: 'none' });
		}
	});
};

AvailabilityMap.prototype._popupMatchesResidenceType = function($popup) {
	if (!this.selectedResidenceType) return true;
	var ids = $popup.attr('data-unit-category-ids');
	if (!ids) return false;
	var arr = ids.split(',').map(function(id) { return id.trim(); });
	return arr.indexOf(this.selectedResidenceType) !== -1;
};

/**
 * Read the CMS-flagged default floor from the root element.
 * `data-default-floor-index` is 1-based (matches the select option values);
 * returns a 0-based index, or null when unset/invalid so we fall back to the first floor.
 * @returns {number|null}
 */
AvailabilityMap.prototype.getDefaultFloorIndex = function() {
	var raw = this.options.$el.attr('data-default-floor-index');
	if (raw == null || raw === '') return null;

	var oneBased = parseInt(raw, 10);
	if (isNaN(oneBased) || oneBased < 1) return null;

	var zeroBased = toZeroBased(oneBased);
	if (zeroBased >= this.$floorImages.length) return null;

	return zeroBased;
};

/**
 * @param {number} [floorIndex=0] - 0-based floor index. Defaults to first floor.
 */
AvailabilityMap.prototype.setInitialFloor = function(floorIndex) {
	var index = (floorIndex == null) ? 0 : floorIndex;
	if (index < 0 || index >= this.$floorImages.length) {
		console.warn(
			'AvailabilityMap: Invalid floor index:', index,
			'Available floors:', this.$floorImages.length
		);
		return;
	}

	this.changeFloor(index);

	var publishedFloor = toOneBased(index);
	setTimeout(function() {
		PubSub.publish('availabilityMap:initialFloorSet', { floorIndex: publishedFloor });
	}, INITIAL_FLOOR_PUBLISH_DELAY_MS);
};

/**
 * @param {number} floorIndex - 0-based index into $floorImages.
 */
AvailabilityMap.prototype.changeFloor = function(floorIndex) {
	if (floorIndex < 0 || floorIndex >= this.$floorImages.length) {
		console.warn(
			'AvailabilityMap: Invalid floor index:', floorIndex,
			'Available floors:', this.$floorImages.length
		);
		return;
	}

	var self = this;

	if (this.currentFloorIndex !== null) {
		this.$floorImages.eq(this.currentFloorIndex).removeClass('active');
	}

	var $newImage = this.$floorImages.eq(floorIndex).addClass('active');
	var imgEl = $newImage[0];
	if (imgEl) {
		if ('loading' in imgEl && imgEl.loading === 'lazy') imgEl.loading = 'eager';

		var afterReady = function() { self.schedulePinPositionUpdate(); };
		if (typeof imgEl.decode === 'function') {
			imgEl.decode().then(afterReady, afterReady);
		} else if (imgEl.complete && imgEl.naturalWidth) {
			afterReady();
		} else {
			$newImage.one('load error', afterReady);
		}
	}

	this.currentFloorIndex = floorIndex;
	var publishedFloor = toOneBased(floorIndex);

	this._closeOffFloorPopups(publishedFloor);

	PubSub.publish('availabilityMap:floorChanged', { floorIndex: publishedFloor });
	this.applyFilters();
};

/**
 * Reset any popup whose floor doesn't match the new active floor so a
 * mid-transition tooltip can't keep pointer-events / z-index over the visible map.
 */
AvailabilityMap.prototype._closeOffFloorPopups = function(activeFloorOneBased) {
	this.options.$el.find(POPUP_SELECTORS).each(function() {
		var $root = $(this);
		var fi = parseInt($root.attr('data-floor-index'), 10);
		if (isNaN(fi) || fi === activeFloorOneBased) return;

		var $wrapper = $root.find(POPUP_WRAPPER_SELECTORS);
		if ($wrapper.length) {
			$wrapper.css({
				opacity: '0',
				visibility: 'hidden',
				pointerEvents: 'none',
				transition: 'none'
			});
			$wrapper.find(POPUP_INNER_SELECTORS).css({
				transform: '',
				transition: 'none'
			});
		}
		$root.css('z-index', '');
	});
};

/**
 * Wire the floor and residence-type <select> filters.
 */
AvailabilityMap.prototype.setupFilterControls = function() {
	var self = this;

	var $floorSelect = this.options.$el.find('select.availability-map_floor-select');
	if ($floorSelect.length) {
		var initialOneBased = String(toOneBased(this.currentFloorIndex || 0));
		setTimeout(function() {
			$floorSelect.val(initialOneBased);
		}, FLOOR_SELECT_SYNC_DELAY_MS);

		$floorSelect.on('change', function() {
			var raw = $(this).val();
			if (raw === '' || raw == null) {
				self.changeFloor(0);
				return;
			}
			var oneBased = parseInt(raw, 10);
			if (!isNaN(oneBased) && oneBased > 0) {
				self.changeFloor(toZeroBased(oneBased));
			}
		});
	}

	var $residenceSelect = this.options.$el.find('.availability-map_bedrooms-select');
	if ($residenceSelect.length) {
		$residenceSelect.on('change', function() {
			self.selectedResidenceType = $(this).val() || null;
			self.applyFilters();
		});
	}
};

AvailabilityMap.prototype.setupResetButton = function() {
	var self = this;
	var $resetButton = this.options.$el.find('.availability-filter_button a');
	if (!$resetButton.length) return;

	$resetButton.on('click', function(e) {
		e.preventDefault();
		self.selectedResidenceType = null;

		var $residenceSelect = self.options.$el.find('.availability-map_bedrooms-select');
		if ($residenceSelect.length) {
			$residenceSelect.val('').trigger('change');
		}
		self.applyFilters();
	});
};

export default AvailabilityMap;
